import { safeFocus } from '@nulo/a11y';
import { createSwiper } from '@nulo/swiper';

/**
 * @extends {HTMLElement}
 */
class ProductSocialProofVideos extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {IntersectionObserver | null} */
  #io = null;

  connectedCallback() {
    const swiperEl = this.querySelector('[data-swiper]');
    if (!(swiperEl instanceof HTMLElement)) return;

    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindModal(signal);
    this.#observeAndInit(swiperEl);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#io = null;
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;

    this.#abortController?.abort();
    this.#abortController = null;
    this.#trigger = null;
  }

  /**
   * @param {HTMLElement} swiperEl
   */
  #observeAndInit(swiperEl) {
    if (this.#swiper) return;

    if (typeof IntersectionObserver !== 'function') {
      this.#initSwiper(swiperEl);
      return;
    }

    if (this.#io) return;

    this.#io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#io?.disconnect();
        this.#io = null;
        this.#initSwiper(swiperEl);
      },
      { rootMargin: '200px 0px' }
    );
    this.#io.observe(this);
  }

  /**
   * @param {HTMLElement} swiperEl
   */
  #initSwiper(swiperEl) {
    if (this.#swiper) return;

    this.#swiper = createSwiper({
      region: this.querySelector('[data-nulo-carousel]'),
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config: {
        slidesPerView: 2.5,
        spaceBetween: 24,
        navigation: {
          nextEl: this.querySelector('[data-social-proof-next]'),
          prevEl: this.querySelector('[data-social-proof-prev]'),
        },
        pagination: {
          el: this.querySelector('[data-social-proof-pagination]'),
          clickable: true,
        },
        breakpoints: {
          640: { slidesPerView: 2.5 },
          1024: { slidesPerView: 3 },
          1280: { slidesPerView: 3.5 },
        },
      },
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindModal(signal) {
    const dialog = this.querySelector('[data-social-proof-dialog]');
    const host = this.querySelector('[data-social-proof-video-host]');
    if (!(dialog instanceof HTMLDialogElement) || !(host instanceof HTMLElement)) return;

    const clearHost = () => {
      host.innerHTML = '';
    };

    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };

    dialog.addEventListener(
      'close',
      () => {
        clearHost();
        if (this.#trigger) {
          safeFocus(this.#trigger);
          this.#trigger = null;
        }
      },
      { signal }
    );

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) closeDialog();
      },
      { signal }
    );

    for (const button of this.querySelectorAll('[data-social-proof-open]')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      button.addEventListener(
        'click',
        () => {
          this.#trigger = button;
          this.#openVideo(button, dialog, host);
        },
        { signal }
      );
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement} host
   */
  #openVideo(button, dialog, host) {
    const mp4 = button.dataset.videoMp4 || '';
    const embed = button.dataset.videoEmbed || '';
    const poster = button.dataset.videoPoster || '';
    const autoplay = button.dataset.videoAutoplay === 'true';
    const title = button.dataset.videoTitle || 'Video';

    host.innerHTML = '';

    if (embed) {
      const iframe = document.createElement('iframe');
      iframe.className = 'product-social-proof-videos__iframe';
      iframe.src = embed;
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
      );
      iframe.setAttribute('allowfullscreen', '');
      iframe.title = title;
      host.appendChild(iframe);
    } else if (mp4) {
      const video = document.createElement('video');
      video.className = 'product-social-proof-videos__video';
      video.controls = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('controlslist', 'nodownload');
      if (poster) video.poster = poster;
      if (autoplay) {
        video.autoplay = true;
        video.muted = true;
      }

      const source = document.createElement('source');
      source.src = mp4;
      source.type = 'video/mp4';
      video.appendChild(source);
      host.appendChild(video);
    } else {
      return;
    }

    if (!dialog.open) dialog.showModal();
  }
}

if (!customElements.get('product-social-proof-videos')) {
  customElements.define('product-social-proof-videos', ProductSocialProofVideos);
}
